import styled from '@emotion/styled';
import { AppTheme } from '@design-system/theme/types';
import { css } from '@emotion/react';
import { IconButtonContainerProps } from '@design-system/atoms/icon-button/icon-button.types';

// Define tone-based color functions
const getToneColor = (theme: AppTheme, tone: string) => {
  switch (tone) {
    case 'error':
      return theme.colors.status.error.dark;
    case 'warning':
      return theme.colors.status.warning.dark;
    case 'info':
      return theme.colors.status.info.dark;
    case 'neutral':
      return theme.colors.technical.black;
    default:
      return theme.colors.primary.normal;
  }
};

// Define variant styles outside the component
const variantStyles = {
  circle: (theme: AppTheme, $size: number, $tone: string) => css`
    border-radius: 50%;
    background-color: ${theme.colors.technical.gray[5]};
    width: ${$size * 2}px;
    height: ${$size * 2}px;

    svg {
      transition: all 0.2s ease;
    }

    &:hover {
      background-color: ${theme.colors.technical.gray[15]};

      svg {
        fill: ${getToneColor(theme, $tone)};
      }
    }

    &:active {
      background-color: ${theme.colors.technical.gray[5]};

      svg {
        fill: ${getToneColor(theme, $tone)};
      }
    }
  `,
  default: (theme: AppTheme, $tone: string) => css`
    background: transparent;

    svg {
      transition: all 0.2s ease;
    }

    &:hover:not([disabled]) {
      background: transparent;

      svg {
        fill: ${getToneColor(theme, $tone)};
      }
    }

    &:active:not([disabled]) {
      color: ${getToneColor(theme, $tone)};
      background: transparent;

      svg {
        fill: ${getToneColor(theme, $tone)};
      }
    }
  `,
  focus: (theme: AppTheme) => css`
    outline: none;

    &:before {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 8px;
      border: 2px solid ${theme.colors.primary.medium};
    }
  `,
  disabled: (theme: AppTheme) => css`
    opacity: 0.5;
    cursor: not-allowed;
    color: ${theme.colors.technical.gray[30]};
  `,
};

export const IconButtonContainer = styled.button<IconButtonContainerProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  color: ${({ theme, $tone = 'default' }) => getToneColor(theme, $tone)};
  cursor: pointer;
  transition: background-color 0.2s ease;

  ${({ $size }) => css`
    width: ${$size}px;
    height: ${$size}px;
  `}

  ${({ $variant = 'default', theme, $size, $tone = 'default' }) =>
    $variant === 'circle' ? variantStyles.circle(theme, $size, $tone) : variantStyles.default(theme, $tone)}
  
  ${({ isFocusVisible, theme }) => isFocusVisible && variantStyles.focus(theme)}

  ${({ isDisabled, theme }) => isDisabled && variantStyles.disabled(theme)}
`;
